import { create } from "zustand"
import useStore from "./store" // <-- import your main store

/**
 * Zustand store for AST navigation and highlighting.
 */
const useASTStore = create((set, get) => ({
  // Width (px) of the graph panel in the architecture diagram; null = default
  architectureGraphWidth: null,
  setArchitectureGraphWidth: (width) => set({ architectureGraphWidth: width }),

  selectedFile: "",
  setSelectedFile: (file) => set({ selectedFile: file }),

  selectedResourseType: "",
  setSelectedResourceType: (type) => set({ selectedResourseType: type }),

  selectedResource: null,
  setSelectedResource: (resource) => set({ selectedResource: resource }),

  /**
   * Efficiently scrolls to and highlights an element with the given astId.
   * @param {string} astId
   */
  gotoAST: (astId) => {
    document.querySelectorAll('[ast_id]').forEach(el => {
      (el instanceof HTMLElement) && (el.style.border = "")
    })
    const elm = document.querySelector(`[ast_id="${astId}"]`)
    if (!elm || !(elm instanceof HTMLElement)) {
      console.warn('Cannot find element with ast:', astId)
      return
    }
    get().scrollIntoView(elm)
    elm.style.border = '3px dashed white'
  },

  /**
   * Navigates to a resource in the UI.
   * @param {string} gotoTab
   * @param {string} gotoAstId
   */
  gotoResource: (gotoTab, gotoAstId) => {
    const state = get()
    const oneReview = useStore.getState().oneReview // <-- get from main store
    if (!oneReview || !oneReview.json || !oneReview.json.tabsObject) return
    const tab = oneReview.json.tabsObject[gotoTab]
    if (!tab) {
      console.warn(gotoTab, 'is not a valid tab')
      return
    }
    if (state.setSelectedResourceType) state.setSelectedResourceType(gotoTab)
    const resource = tab.elements.find(x => x.astId === gotoAstId)
    if (resource && state.setSelectedResource) {
      state.setSelectedResource(resource)
    }
  },

  /**
   * Navigates to a file and line, highlighting the line.
   * @param {string} file
   * @param {number|string} line
   */
  goto: (file, line) => {
    set((state) => {
      state.setSelectedFile(file)
      return {}
    })
    if (line) {
      setTimeout(() => {
        const elm = document.querySelector(`[data-line="${line}"]`)
        if (elm) {
          (elm instanceof HTMLElement) && (elm.style.border = "1px solid yellow")
          setTimeout(() => {
            (elm instanceof HTMLElement) && (elm.style.border = "")
          }, 3000)
          if (elm.scrollIntoView) {
            elm.scrollIntoView({ block: "center", behavior: "smooth" })
          }
        }
      })
    }
  },

  /**
   * Smoothly scrolls an element into view, centering it in its scroll parent.
   * @param {HTMLElement} elm
   */
  scrollIntoView: (elm) => {
    if (!elm) return
    const prn = get().getScrollParent(elm)
    if (prn && prn instanceof HTMLElement) {
      prn.scrollTop = elm.offsetTop - prn.clientHeight / 2
    } else {
      elm.scrollIntoView({ block: "center", behavior: "smooth" })
    }
  },

  /**
   * Finds the nearest scrollable parent of a node.
   * @param {HTMLElement} node
   * @returns {HTMLElement|null}
   */
  getScrollParent: (node) => {
    if (!node) return null
    if (node.scrollHeight > node.clientHeight && getComputedStyle(node).overflowY !== "visible") {
      return node
    }
    const parent = node.parentNode;
    return parent instanceof HTMLElement ? get().getScrollParent(parent) : null;
  },

  // Deep search utility
  deepSearchJSON: (obj = {}, key, value) => {
    const result = [];
    const recursiveSearch = (obj = {}) => {
      if (!obj || typeof obj !== "object") {
        return;
      }
      if (obj[key] === value) {
        result.push(obj);
      }
      Object.keys(obj).forEach((k) => {
        recursiveSearch(obj[k]);
      });
    };
    recursiveSearch(obj);
    return result;
  },

  getTotalProgress: () => {
    const oneReview = useStore.getState().oneReview;
    const completedTasks = useStore.getState().completedTasks || {};
    const json = oneReview?.analyzedData?.json;
    const deepSearchJSON = get().deepSearchJSON;

    if (!json) {
      return { total: 0, completed: 0 };
    }
    const totalTasks = deepSearchJSON(json.tabsArray, "type", "task");
    const completedTasksCount = Object.values(completedTasks).filter(
      (value) => value === true
    ).length;

    return {
      total: totalTasks.length,
      completed: completedTasksCount,
    };
  },

  getProgressForTab: (tabId) => {
    const oneReview = useStore.getState().oneReview;
    const completedTasks = useStore.getState().completedTasks || {};
    
    const json = oneReview?.analyzedData?.json;
    const deepSearchJSON = get().deepSearchJSON;

    if (!json) {
      return { total: 0, completed: 0 };
    }

    const tabJson = json.tabsObject[tabId];
    const totalTabTasks = tabJson ? deepSearchJSON(tabJson, "type", "task") : [];

    const elements = json.tabsObject[tabId]?.elements || [];
    const completedTaskSet = new Set();

    elements.forEach((element) => {
      if (element.inUse?.length > 0 || !Object.prototype.hasOwnProperty.call(element, "inUse")) {
        element.cards.forEach((card) => {
          card.cardElements.forEach((cardElement) => {
            if (cardElement.type === "task") {
              const key = `${card.id}_${cardElement.id}`;
              if (completedTasks[key] === true) {
                completedTaskSet.add(key);
              }
            }
          });
        });
      }
    });

    return {
      total: totalTabTasks.length,
      completed: completedTaskSet.size,
    };
  },

  getProgressForResource: (astId) => {
    const oneReview = useStore.getState().oneReview;
    const completedTasks = useStore.getState().completedTasks || {};
    const selectedResourseType = get().selectedResourseType;
    const json = oneReview?.analyzedData?.json;
    const deepSearchJSON = get().deepSearchJSON;

    if (!json) {
      return { total: 0, completed: 0 };
    }

    const resourceJson = json.tabsObject[selectedResourseType]?.elements.find(
      (x) => x.astId === astId
    );
    const totalTabTasks = deepSearchJSON(resourceJson, "type", "task");

    const elements = json.tabsObject[selectedResourseType]?.elements.filter(
      (element) => element.astId === astId
    );
    const completedTaskSet = new Set();

    elements.forEach((element) => {
      if (element.inUse?.length > 0 || !Object.prototype.hasOwnProperty.call(element, "inUse")) {
        element.cards.forEach((card) => {
          card.cardElements.forEach((cardElement) => {
            if (cardElement.type === "task") {
              const key = `${card.id}_${cardElement.id}`;
              if (completedTasks[key] === true) {
                completedTaskSet.add(key);
              }
            }
          });
        });
      }
    });

    return {
      total: totalTabTasks.length,
      completed: completedTaskSet.size,
    };
  },

  // ...rest of your store...
}))

export default useASTStore